// eslint-disable-next-line @typescript-eslint/no-unused-vars
import React, { useContext, useEffect, useState } from 'react';
import { classes, useSlug } from '@bloomberg/media-ui-core';
import { adOptionsContext } from '@foundation/sparkle/lib/AdOptionsContext';
import DecoupledSparkleAd from '@foundation/sparkle/lib/DecoupledSparkleAd';
import SparkleAd from '@foundation/sparkle/lib/SparkleAd';
import { SparkleAdOptions } from '@foundation/sparkle/lib/types';

import useAdRendered from '../hooks/useAdRendered';
import { AdType, GlobalAdProps } from '../types/index';
import { buildAdId, getAdOptions } from '../utils/dimensions';

import styles from './BaseAd.module.css';

export type BaseAdProps = GlobalAdProps & {
  adType: AdType;
  isDecoupled?: boolean;
};

export const BaseAd = React.forwardRef<HTMLDivElement, BaseAdProps>(
  function BaseAdComponent(props: BaseAdProps, ref) {
    const {
      name = 'BaseAd',
      showPlaceholder = true,
      hideEmptyAd = false,
      positionIncrement,
      adType,
      adOptions,
      unstyled,
      className,
      adHeaderText,
      isDecoupled = false,
    } = props;

    const { adOptions: contextAdOptions } = useContext(adOptionsContext);
    const { disableAds } = contextAdOptions || {};
    const { sizeRendered, onAdRender, onEmptyRender, adStatus } =
      useAdRendered(props);
    const [adId, setAdId] = useState('');
    const componentName = useSlug(name);
    const cx = classes(
      [
        styles.baseAd,
        {
          [styles.adPlaceholder]: showPlaceholder,
          [styles.rendered]: Boolean(sizeRendered),
          [styles.hideEmptyAd]: Boolean(hideEmptyAd),
        },
      ],
      { unstyled, className }
    );
    const options: SparkleAdOptions = {
      // Prevents ad creation if there is no adId
      disableAds: !adId,
      ...getAdOptions(adType, positionIncrement),
      ...adOptions,
    };

    useEffect(() => {
      const idModifier = Math.floor(Math.random() * 100_000).toString();

      setAdId(buildAdId(adType, idModifier, positionIncrement));
    }, []);

    if (disableAds) return null;

    return (
      <div
        className={cx}
        ref={ref}
        data-component={componentName}
        data-ad-size={sizeRendered}
        data-ad-status={adStatus}
      >
        {adHeaderText && <div className={styles.adHeader}>{adHeaderText}</div>}
        {isDecoupled && adType === 'leaderboard' ? (
          <DecoupledSparkleAd
            positionIncrement={positionIncrement}
            id={adId}
            options={options}
            onRender={onAdRender}
            onEmpty={onEmptyRender}
          />
        ) : (
          <SparkleAd
            id={adId}
            options={options}
            onRender={onAdRender}
            onEmpty={onEmptyRender}
          />
        )}
      </div>
    );
  }
);
